import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Phone, ChevronRight, Loader2, Flame, Target, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import AIScoreBadge from "./entity-detail/AIScoreBadge";
import { tierConfig } from "./entity-detail/AIScoreBadge";

/**
 * Widget Dashboard "Top N prospects chauds à appeler aujourd'hui".
 *
 * Affiche les prospects + opportunités triés par score IA décroissant,
 * avec click-to-call Aircall direct (clic icône téléphone) ou navigation
 * vers la fiche complète (clic carte).
 *
 * Le widget se masque automatiquement si aucun item scoré n'existe.
 */
export default function HotLeadsWidget({ limit = 5, minScore = 50, testId = "hot-leads-widget" }) {
  const { axiosAuth } = useAuth();
  const { societeApi } = useSociete?.() || {};
  const api = societeApi ? societeApi() : axiosAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/crm/leads/hot-list?limit=${limit}&min_score=${minScore}`);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, minScore]);

  useEffect(() => {
    reload();
  }, [reload]);

  const callNow = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    if (!item.phone) {
      alert("Pas de numéro de téléphone enregistré pour ce contact.");
      return;
    }
    setCalling(item.entity_id);
    try {
      const body = item.kind === "prospect"
        ? { prospect_id: item.entity_id, phone: item.phone }
        : { phone: item.phone };
      await api.post("/aircall/click-to-call", body);
      // light feedback : reload to bring fresh KPIs eventually
    } catch (err) {
      alert(err.response?.data?.detail || "Aircall indisponible. Vérifiez la configuration de l'intégration.");
    } finally {
      setCalling(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-pulse" data-testid={`${testId}-loading`}>
        <div className="h-4 w-40 bg-white/5 rounded mb-3" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 bg-white/5 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!items.length) {
    return null; // Graceful degradation : pas de leads scorés → widget caché
  }

  return (
    <div
      className="bg-gradient-to-br from-[#1a1525] to-[#0e0a17] border border-[#f7c935]/20 rounded-xl p-5 relative overflow-hidden"
      data-testid={testId}
    >
      {/* Background glow */}
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-rose-500/10 blur-3xl pointer-events-none" />

      <div className="flex items-center justify-between mb-4 relative">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center text-base shadow-lg shadow-rose-500/20">
            <Flame className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">À rappeler aujourd'hui</h3>
            <p className="text-[10px] text-slate-500">Top {items.length} leads scorés par l'IA</p>
          </div>
        </div>
        <Link
          to="/admin/leads-chauds"
          className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1 transition-colors"
          data-testid="hot-leads-see-all"
        >
          Tout voir <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="space-y-2 relative">
        {items.map((it) => {
          const cfg = tierConfig(it.tier);
          const initials = (it.name || "?")
            .split(" ")
            .map((s) => s[0])
            .filter(Boolean)
            .slice(0, 2)
            .join("")
            .toUpperCase();
          const action = it.next_actions?.[0];
          return (
            <Link
              key={`${it.kind}_${it.entity_id}`}
              to={it.url}
              className="block bg-[#0b0811] hover:bg-[#14101c] border border-white/5 hover:border-white/15 rounded-lg p-3 transition-all hover:-translate-y-0.5 group"
              data-testid={`hot-lead-${it.entity_id}`}
            >
              <div className="flex items-center gap-3">
                {/* Avatar lettre */}
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-md ${cfg.bg} ${cfg.text} border ${cfg.border}`}
                >
                  {initials || "?"}
                </div>

                {/* Identité */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-white truncate">{it.name}</span>
                    <AIScoreBadge tier={it.tier} score={it.score} size="sm" testId={`badge-${it.entity_id}`} />
                  </div>
                  {it.company && <div className="text-[10px] text-slate-500 truncate">{it.company}</div>}
                  {action && (
                    <div className="text-[10px] text-amber-300/80 mt-0.5 flex items-center gap-1 truncate">
                      <Target className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{action}</span>
                    </div>
                  )}
                </div>

                {/* Click-to-call */}
                {it.phone && (
                  <button
                    onClick={(e) => callNow(e, it)}
                    disabled={calling === it.entity_id}
                    className="w-9 h-9 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300 flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-50"
                    title={`Appeler ${it.phone} via Aircall`}
                    data-testid={`call-now-${it.entity_id}`}
                  >
                    {calling === it.entity_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                  </button>
                )}
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
